import { useQuery } from 'react-query';
import { getPatientNutrients } from '../api/getPatientNutrients';
import useNutrientDv from './useNutrientDv';
import { useNutrientsContext } from '../contexts/NutrientsContext';
import { NUTRIENT_KEYS, NUTRIENT_KEYS_ALL } from '../constants/constants';

// The key of "Total Carbohydrates" is changed to "Carbohydrates"
const getNutrientName = (name) => {
  if (name === 'Total Carbohydrates') {
    return 'Carbohydrates';
  }
  return name;
};

const getNutrientData = ({ nutrientsData, nutrientDvData, nutrientType }) => {
  const keysToFind = nutrientType === 'all' ? NUTRIENT_KEYS_ALL : Object.values(NUTRIENT_KEYS[nutrientType] || {});

  const getNutrientDescription = (nutrient) => {
    if (!nutrientDvData || !nutrient) return '';
    const description = nutrientDvData[nutrient]?.longDescription;
    if (!description) return '';
    return description;
  };

  const getNutrientDietaryValue = (nutrient) => {
    if (!nutrientDvData || !nutrient) return 0;
    const dvData = nutrientDvData[nutrient]?.dv;
    if (!dvData) return 0;

    // Return first non-null value, or 0 if both are null
    return dvData[0] ?? dvData[1] ?? 0;
  };

  // Return an ordered array instead of an object
  const orderedNutrients = [];

  if (nutrientsData?.diet_id_nutrition_info) {
    // Create a map for easy lookup
    const nutritionInfoMap = {};
    nutrientsData.diet_id_nutrition_info.forEach((info) => {
      if (info?.key) {
        nutritionInfoMap[info.key] = info;
      }
    });

    // Process keys in the order they appear in keysToFind
    keysToFind.forEach((key) => {
      if (!key) return;
      const info = nutritionInfoMap[key];
      if (info) {
        const isEnergy = key === NUTRIENT_KEYS.macronutrients.ENERGY_KILOCALORIES;
        const currentValue = Math.round(info?.value || 0);
        orderedNutrients.push({
          key,
          id: key
            .split('-')
            .map((word, index) => {
              if (index === 0) return word;
              return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(''),
          name: getNutrientName(info?.label?.trim() || ''),
          nutrientKey: key,
          unit: info?.unit || '',
          current: currentValue,
          goal: isEnergy ? currentValue : Math.round(getNutrientDietaryValue(key)),
          description: getNutrientDescription(key),
        });
      }
    });
  }
  return orderedNutrients;
};

const useNutrients = (workflowToken, type) => {
  const { state: { allNutrientsData, allNutrientsLoading } = {} } = useNutrientsContext();
  const { data: nutrientDvData, isLoading: nutrientDvLoading } = useNutrientDv(workflowToken);

  // If we're in the context provider, fetch all data
  if (type === 'all') {
    const { data, isLoading, isRefetching, refetch } = useQuery(
      `nutrients_all_${workflowToken}`,
      () => getPatientNutrients(workflowToken),
      {
        enabled: !!workflowToken,
        refetchOnWindowFocus: false,
        staleTime: 3600,
      },
    );

    const nutrientProfile = getNutrientData({
      nutrientsData: data,
      nutrientDvData: nutrientDvData?.data,
      nutrientType: type,
    });

    return {
      data: nutrientProfile,
      isLoading: isLoading || nutrientDvLoading || isRefetching,
      refetch,
    };
  }

  // If we're in a component using the context, filter the existing data
  const nutrientProfile = getNutrientData({
    nutrientsData: allNutrientsData,
    nutrientDvData: nutrientDvData?.data,
    nutrientType: type,
  });

  return {
    data: nutrientProfile,
    isLoading: allNutrientsLoading || nutrientDvLoading,
  };
};

export default useNutrients;
